import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { IStandardTranscriptNormalized } from '../../shared/models/standard-transcript-normalized.model';
import { IConversationDetails } from '../../shared/models/conversation-details.model';
import { PdfTranscriptionComponent } from '../../shared/components/pdf/components/pdf-transcription/pdf-transcription.component';
import { PdfScorecardComponent } from '../../shared/components/pdf/components/pdf-scorecard/pdf-scorecard.component';
import html2pdf from 'html2pdf.js';
import { IScorecard, ScorecardModel } from '../../shared/models/scorecard.model';
import { IScorecardPreview } from '../../shared/models/scorecard-preview.model';
import { PdfDetailsComponent } from '../../shared/components/pdf/components/pdf-details/pdf-details.component';
import { IContactDetails } from '../../shared/models/contact-details.model';
import { IAppointmentDetails } from '../../shared/models/appointment-details.model';
import { ILeasingPreferences } from '../../shared/models/leasing-preferences.model';
import { ICampaignDetails } from '../../shared/models/campaign-details.model';

const OPTIONS = {
  margin: 20,
  pagebreak: { mode: ['avoid-all', 'legacy'] },
  jsPDF: { unit: 'px', hotfixes: ['px_scaling'] },
};

@Injectable({ providedIn: 'root' })
export class PdfExportService {
  private createdComponents: ComponentRef<any>[] = [];

  constructor(
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
  ) {}

  async generatePdf(data: IPdfExportData): Promise<void> {
    const { conversationDetails, startDate, transcription, userName, scorecards = [] } = data;
    const filename = this.getFileName(startDate);
    const wrapper = this.createWrapper();

    if (transcription) {
      const element = this.createTranscriptionComponent(transcription, userName);
      wrapper.appendChild(element);
    }

    if (conversationDetails) {
      const element = this.createDetailsComponent(data);
      wrapper.appendChild(element);
    }

    if (scorecards?.length) {
      for (const [i, scorecard] of scorecards.entries()) {
        const element = this.createScorecardComponent(scorecard, i === scorecards.length - 1);
        wrapper.appendChild(element);
      }
    }
    this.appRef.tick();
    await html2pdf()
      .from(wrapper)
      .set({ ...OPTIONS, filename })
      .save();

    document.body.removeChild(wrapper);
    this.destroyComponents();
  }

  private createWrapper(): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.style.width = '100%';
    document.body.appendChild(wrapper);
    return wrapper;
  }

  private createTranscriptionComponent(
    transcription: IPdfExportData['transcription'],
    userName: IPdfExportData['userName'],
  ): HTMLElement {
    const { componentRef, element } = this.createComponent(PdfTranscriptionComponent);
    componentRef.instance.transcription = transcription;
    componentRef.instance.userName = userName;
    this.createdComponents.push(componentRef);
    return element;
  }

  private createDetailsComponent({
    contactDetails,
    appointmentDetails,
    leasingPreferences,
    campaignDetails,
    conversationDetails,
  }: IPdfExportData): HTMLElement {
    const { componentRef, element } = this.createComponent(PdfDetailsComponent);
    componentRef.instance.conversationDetails = conversationDetails;
    componentRef.instance.contactDetails = contactDetails;
    componentRef.instance.appointmentDetails = appointmentDetails;
    componentRef.instance.leasingPreferences = leasingPreferences;
    componentRef.instance.campaignDetails = campaignDetails;
    this.createdComponents.push(componentRef);
    return element;
  }

  private createScorecardComponent(scorecard: IPdfScorecard, isLast: boolean): HTMLElement {
    const { componentRef, element } = this.createComponent(PdfScorecardComponent);
    componentRef.instance.scorecard = scorecard;
    componentRef.instance.isLastScorecard = isLast;
    this.createdComponents.push(componentRef);
    return element;
  }

  private createComponent<T>(component: Type<T>): {
    componentRef: ComponentRef<T>;
    element: HTMLElement;
  } {
    const componentRef = createComponent(component, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector,
    });

    this.appRef.attachView(componentRef.hostView);
    const element = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    return { componentRef, element };
  }

  private getFileName(startDate: string): string {
    const agentName = '';
    const formattedDate = new Date(startDate).toISOString().split('T')[0];
    return `Conversation_${formattedDate}_${agentName}.pdf`;
  }

  private destroyComponents(): void {
    for (const componentRef of this.createdComponents) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }
    this.createdComponents = [];
  }
}

export class PdfScorecardModel extends ScorecardModel implements IPdfScorecard {
  scoreAchieved: number;
  totalPossiblePoints: number;

  constructor(data: IPdfScorecard) {
    super(data);

    this.scoreAchieved = data.scoreAchieved;
    this.totalPossiblePoints = data.totalPossiblePoints;
  }
}

export interface IPdfScorecard extends IScorecard, Pick<IScorecardPreview, 'scoreAchieved' | 'totalPossiblePoints'> {}

export interface IPdfExportData {
  contactDetails: IContactDetails | null;
  appointmentDetails: IAppointmentDetails | null;
  leasingPreferences: ILeasingPreferences | null;
  campaignDetails: ICampaignDetails | null;
  conversationDetails: IConversationDetails | null;
  scorecards: IPdfScorecard[] | null;
  transcription: IStandardTranscriptNormalized | null;
  userName: string | null;
  startDate: string;
}
